/**
 * Upgrade / Paywall screen — shows the 3-tier comparison with current plan
 * highlighted. Wires up:
 *   - "Empezar prueba" → POST /subscription/start-trial (manual, free 7-day Pro).
 *   - "Comprar" → calls RevenueCat native paywall on iOS/Android. On web
 *     (no native shell), shows a "Disponible en la app" notice and offers
 *     the admin-override button (Beta) so the user can keep testing.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, X, Sparkles, Loader2, ArrowLeft, Crown, ShieldCheck, Zap } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import { useSubscription, featureLabel, tierLabel } from "@/lib/subscription";
import { isRunningNative } from "@/lib/native";

const TIER_ICONS = { basic: ShieldCheck, premium: Zap, pro: Crown };
const TIER_ACCENT = {
  basic: "from-slate-50 to-white border-slate-200",
  premium: "from-blue-50 to-white border-blue-200",
  pro: "from-amber-50 to-white border-amber-300 ring-1 ring-amber-300",
};
const FEATURE_GROUPS = [
  { title: "Esencial", items: ["core_crud", "catalog_export", "whatsapp_chat"] },
  { title: "Operación", items: ["inventory", "invoicing", "receivables", "costing", "purchase_orders", "backup"] },
  { title: "WhatsApp", items: ["whatsapp_share", "whatsapp_advanced", "public_links"] },
  { title: "Inteligencia", items: ["ai_order_image", "insights", "route_planning"] },
];

function PriceTag({ usd }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="text-3xl font-black tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>${usd}</span>
      <span className="text-sm text-slate-500">/ mes USD</span>
    </div>
  );
}

export default function Upgrade() {
  const [catalog, setCatalog] = useState(null);
  const [busyTier, setBusyTier] = useState("");
  const { sub, tier, reload, isAdmin } = useSubscription();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reason = params.get("reason");
  const fromTier = params.get("from") || tier;

  useEffect(() => {
    api.get("/subscription/catalog").then(r => setCatalog(r.data)).catch(() => setCatalog(null));
  }, []);

  const startTrial = async (targetTier) => {
    setBusyTier(targetTier);
    try {
      await api.post("/subscription/start-trial", { tier: targetTier });
      toast.success(`¡Prueba de ${tierLabel(targetTier)} iniciada por 7 días!`);
      await reload();
      window.dispatchEvent(new Event("subscription-updated"));
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la prueba");
    } finally {
      setBusyTier("");
    }
  };

  const buyOnNative = async (targetTier) => {
    if (!isRunningNative()) {
      toast.info("La compra está disponible solo en la app de iOS/Android. Estamos en preview web.");
      return;
    }
    setBusyTier(targetTier);
    try {
      const { triggerNativePurchase } = await import("@/lib/iap");
      await triggerNativePurchase(targetTier);
      await reload();
      window.dispatchEvent(new Event("subscription-updated"));
      navigate("/");
    } catch (e) {
      toast.error(e?.message || "No se pudo completar la compra");
    } finally {
      setBusyTier("");
    }
  };

  const adminOverride = async (targetTier) => {
    setBusyTier(targetTier);
    try {
      await api.post(`/subscription/admin-override?tier=${targetTier}`);
      toast.success(`Plan cambiado a ${tierLabel(targetTier)} (override beta)`);
      await reload();
      window.dispatchEvent(new Event("subscription-updated"));
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar");
    } finally {
      setBusyTier("");
    }
  };

  if (!catalog) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
    </div>
  );

  const trialUsed = sub?.trial_used;

  return (
    <div className="space-y-6 pb-12" data-testid="upgrade-page">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/" data-testid="upgrade-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Volver
            </Link>
          </Button>
          <h1 className="text-3xl sm:text-4xl font-black mt-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Elegí tu plan
          </h1>
          <p className="text-slate-600 mt-1">
            Tu plan actual: <span className="font-semibold text-[#0B1F3A]">{tierLabel(tier)}</span>
            {sub?.status === "trial" && sub?.trial_ends_at && (
              <span className="ml-2 text-amber-700">· prueba hasta {new Date(sub.trial_ends_at).toLocaleDateString("es-CR")}</span>
            )}
          </p>
        </div>
      </div>

      {reason && (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm" data-testid="upgrade-reason-banner">
          <strong>Para usar {featureLabel(reason)}</strong> necesitás un plan superior al actual ({tierLabel(fromTier)}). Mirá las opciones abajo. ↓
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {catalog.tiers.map(t => {
          const Icon = TIER_ICONS[t];
          const price = catalog.pricing[t]?.monthly_usd ?? 0;
          const isCurrent = tier === t;
          return (
            <div
              key={t}
              data-testid={`plan-card-${t}`}
              className={`relative rounded-xl bg-gradient-to-br ${TIER_ACCENT[t]} border p-5 flex flex-col`}
            >
              {t === "pro" && (
                <span className="absolute -top-2 right-4 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                  Recomendado
                </span>
              )}
              <div className="flex items-center gap-2">
                <Icon className={`h-5 w-5 ${t === "pro" ? "text-amber-600" : t === "premium" ? "text-blue-600" : "text-slate-500"}`} />
                <h2 className="text-xl font-black" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>{tierLabel(t)}</h2>
                {isCurrent && (
                  <span className="ml-auto text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold" data-testid={`current-plan-${t}`}>
                    Actual
                  </span>
                )}
              </div>
              <div className="mt-3"><PriceTag usd={price} /></div>
              <p className="text-xs text-slate-500 mt-1">
                Clientes y productos ilimitados ·
                {" "}{catalog.limits[t].sellers ?? "∞"} vendedor{(catalog.limits[t].sellers ?? 2) === 1 ? "" : "es"}
              </p>

              <div className="mt-4 space-y-1.5 text-sm">
                {FEATURE_GROUPS.flatMap(g => g.items).map(k => {
                  const tiersFor = catalog.features[k] || [];
                  const included = tiersFor.includes(t);
                  return (
                    <div key={k} className="flex items-start gap-2">
                      {included ? <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <X className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" />}
                      <span className={included ? "text-slate-800" : "text-slate-400 line-through"}>{featureLabel(k)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 space-y-2">
                {isCurrent ? (
                  <Button disabled className="w-full" data-testid={`btn-current-${t}`}>Plan actual</Button>
                ) : t === "basic" ? (
                  <Button variant="outline" disabled className="w-full">Gratis</Button>
                ) : (
                  <>
                    {!trialUsed && isAdmin && (
                      <Button
                        onClick={() => startTrial(t)}
                        disabled={busyTier === t}
                        className="w-full bg-[#0B1F3A] hover:bg-[#152C57] text-white"
                        data-testid={`btn-trial-${t}`}
                      >
                        {busyTier === t ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                        Probar 7 días gratis
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => buyOnNative(t)}
                      disabled={busyTier === t}
                      className="w-full"
                      data-testid={`btn-buy-${t}`}
                    >
                      Suscribirse {isRunningNative() ? "" : "(en la app móvil)"}
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => adminOverride(t)}
                        disabled={busyTier === t}
                        className="w-full text-xs text-slate-500 hover:text-slate-700"
                        data-testid={`btn-override-${t}`}
                        title="Solo en preview/Beta — fuerza el plan sin cobrar"
                      >
                        Beta: activar sin pago
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-slate-500 mt-4 space-y-1">
        <p>
          Los pagos se procesan a través de Apple App Store y Google Play. Podés cancelar en cualquier momento desde la configuración de tu cuenta de tienda. La prueba gratis se activa por 7 días una sola vez.
        </p>
        <p>
          <strong>Catálogo público / Enlaces para clientes:</strong> en Premium se muestra una pequeña marca "Powered by MiNegocio" al pie. En Pro se quita.
        </p>
      </div>
    </div>
  );
}
